import { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiProblem, qk } from '@cowoo/shared';
import { LoadingScreen, StateScreen } from '@cowoo/shared/ui-web';
import { api } from '../../lib/api';
import { startSse, stopSse } from '../../lib/sse';
import { useAdminStore } from '../../lib/store';

/**
 * The backoffice gate (10-web-admin § 2, 06-auth § 4.2).
 *
 * One question: does this account have a role? `403 not_admin` → `/kein-zugang`.
 *
 * D-034 required a second factor here until it was revised on 2026-09-28. The
 * server no longer asks for one, so neither does this.
 */
export function Gate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAdminStore((state) => state.token);
  const profile = useAdminStore((state) => state.profile);
  const setProfile = useAdminStore((state) => state.setProfile);

  const me = useQuery({
    queryKey: qk.admin.area('me'),
    enabled: token !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/me');
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (!me.data) return;
    setProfile({
      userId: me.data.userId,
      role: me.data.role,
      capabilities: me.data.capabilities,
      email: me.data.email,
      name: me.data.name,
      totpEnabled: me.data.totpEnabled,
    });
    // § 2.2 step 4 — the stream starts once there is a role behind it.
    startSse(queryClient);
    return () => stopSse();
  }, [me.data, setProfile, queryClient]);

  useEffect(() => {
    if (!(me.error instanceof ApiProblem)) return;
    if (me.error.code === 'not_admin') navigate('/kein-zugang', { replace: true });
  }, [me.error, navigate]);

  if (!token) return <Navigate to="/login" replace />;
  if (me.isError) return <StateScreen title={t('errors.unknown')} />;
  /**
   * Nothing below the gate renders before the role is in the store.
   *
   * `setProfile` runs in an effect, and React runs a child's effects before its
   * parent's — so on the commit that first renders the children, the store is
   * still empty. Any child that *decides* something from capabilities decides
   * it wrong exactly once: `Home` read an empty capability list and sent a
   * signed-in owner to `/kein-zugang`.
   */
  if (me.isLoading || !profile) return <LoadingScreen label={t('common.loading')} />;
  return <Outlet />;
}
